// Versão: 3.24
// Data..: 2026-10-15
// v3.24: painel "Relacionar / Mesclar" vira seletor com busca de chamados abertos
// (multi-seleção por chips, filtro no cliente por número/assunto/solicitante); substitui
// o input de números digitados e passa a mesclar por ids reais.
// v3.23: card "Horas" avisa quando o chamado NÃO está vinculado a uma empresa (as horas
// não entram no relatório de horas de suporte por empresa); apontamento não é bloqueado.
// v3.22: Tempo de execução (cronômetro) — painel Detalhes mostra "Tempo de execução"
// (HH:MM, "em andamento" com ciclo aberto, "—" nos antigos) e o painel Ações ganha
// botão Pausar/Retomar cronômetro (Pausa Atendente) via /tickets/:id/timer/pause|resume.
// v3.21: cliente dono pode reabrir o chamado (botão "Reabrir chamado") dentro de 3 dias
// do fechamento; passado o prazo, mostra aviso "Prazo de reabertura expirado — abra um
// novo chamado" no lugar do botão. O backend reforça dono + prazo.
// v3.20: página de detalhe usa .full-bleed (ocupa toda a largura do painel, sem o
// limite de 1100px); conteúdo de e-mail HTML passa a caber na bolha (ver .msg-body
// no theme.css) — telas largas aproveitam melhor o espaço.
// v3.19: botão "Excluir chamado" (admin) no card Ações — Modal de confirmação avisa que
// apaga o histórico (irreversível), chama DELETE /tickets/:id e navega para /tickets.
// v3.16: seletor de Empresa no card Ações (staff) — permite definir/trocar a empresa
// do chamado manualmente (a associação automática por domínio/cliente continua valendo).
// v3.18: "Iniciar atendimento" também atribui o chamado ao usuário logado quando
// ainda não há responsável (num único passo).
// v3.17: anexo de imagem abre em popup (ImageThumb) em vez de nova aba.
// v3.16: Ctrl+Enter envia a resposta; botão "Adicionar usuário" cria um acesso de
// cliente vinculado a uma empresa (modal), a partir do chamado.
// v3.15: anexos de imagem/vídeo/áudio da mensagem aparecem inline no chamado
// (antes só mostravam o nome do arquivo) — imagens recebidas por WhatsApp são exibidas.
// v3.14: mostra o departamento para o qual o chamado foi aberto (selo em Detalhes).
// v3.13: click-to-call (3CX) — botão de ligar no Contato do solicitante e no
// Telefone/Ramal interno da empresa; registra a ligação como nota interna.
// v3.12: layout principal + aside usa .page-2col (colapsa em 1 coluna no mobile).
// Detalhe do chamado: thread (chat) + responder com anexos e nota interna,
// painel lateral com dados e ações inline (status, prioridade, atribuir, departamento,
// classificação, diagnóstico, checklist, aprovações, visitas, ativo do CMDB, horas,
// seguidores, relacionar/mesclar), selos de SLA e timeline de eventos.
// v3.07: encerramento guiado — resolver/fechar abre popup exigindo Causa (opcional)
// e Solução (obrigatória) com editor rico (colar imagem/anexo); caixa de status destacada.
// v3.08: Checklist logo abaixo de Ações + checklist hierárquico (categorias › subitens).
// v3.09: composer "Responder" no topo (após o assunto); mensagens em ordem
// decrescente (mais recente primeiro); mensagens longas recolhidas com "Ler mais".
// v3.10: ao resolver/fechar o chamado, volta para a lista de chamados.
// v3.11: painel "Empresa" no chamado com dados cadastrais (documento, telefone,
// e-mail, endereço, domínios) para consulta rápida do técnico.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  TicketStatus,
  TicketPriority,
  MessageDirection,
  UserRole,
  Impact,
  Urgency,
  ImpactLabels,
  UrgencyLabels,
  AssetTypeLabels,
  TicketEventLabels,
  TicketEventIcons,
  ApprovalStatusLabels,
  VisitStatus,
  VisitStatusLabels,
  type Ticket,
  type Message,
  type Attachment,
  type Department,
  type Category,
  type Asset,
  type Company,
  type TicketEvent,
  type TimeEntry,
  type ChecklistItem,
  type Approval,
  type Visit,
  type User,
  type Paginated,
} from '@makweb/shared';
import { api } from '../lib/api.js';
import { uploadFile } from '../lib/upload.js';
import { config } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import { StatusBadge, PriorityBadge } from '../components/Badge.js';
import { RichEditor } from '../components/RichEditor.js';
import { CallButton } from '../components/CallButton.js';
import { Modal } from '../components/Modal.js';
import { ImageThumb } from '../components/ImageThumb.js';
import { buildCategoryOptions, categoryPath } from '../lib/categories.js';

interface TicketDetail {
  ticket: Ticket;
  messages: Message[];
}

const statusOptions = [
  { v: TicketStatus.OPEN, l: 'Aberto' },
  { v: TicketStatus.TRIAGE, l: 'Triagem' },
  { v: TicketStatus.IN_PROGRESS, l: 'Em atendimento' },
  { v: TicketStatus.WAITING_CLIENT, l: 'Aguardando cliente' },
  { v: TicketStatus.WAITING_THIRD_PARTY, l: 'Aguardando terceiro' },
  { v: TicketStatus.SCHEDULED, l: 'Agendado' },
  { v: TicketStatus.RESOLVED, l: 'Resolvido' },
  { v: TicketStatus.CLOSED, l: 'Fechado' },
];

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const isStaff = user?.role === UserRole.ADMIN || user?.role === UserRole.AGENT;
  const fileRef = useRef<HTMLInputElement>(null);

  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [timeline, setTimeline] = useState<TicketEvent[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [survey, setSurvey] = useState<{ rating?: number; comment?: string; answeredAt?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [sending, setSending] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [agents, setAgents] = useState<User[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [canned, setCanned] = useState<{ id: string; title: string; body: string }[]>([]);
  const [articles, setArticles] = useState<{ id: string; title: string }[]>([]);
  // Modal "Adicionar usuário" (criar acesso de cliente vinculado a uma empresa).
  const [addUserOpen, setAddUserOpen] = useState(false);
  // Exclusão do chamado (admin): modal de confirmação + estado de progresso.
  const isAdmin = user?.role === UserRole.ADMIN;
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    if (!id) return;
    api.get<TicketDetail>(`/tickets/${id}`).then(setDetail).catch(() => setDetail(null)).finally(() => setLoading(false));
    // Timeline de eventos (falha silenciosa não impede a visualização do chamado).
    api.get<TicketEvent[]>(`/tickets/${id}/events`).then(setTimeline).catch(() => setTimeline([]));
  };
  useEffect(load, [id]);

  // Auto-atualização: recarrega a conversa periodicamente enquanto o chamado está
  // aberto, para que novas mensagens do cliente apareçam sem recarregar a página.
  // Pausa quando a aba não está visível (evita chamadas desnecessárias em background).
  useEffect(() => {
    if (!id) return;
    const REFRESH_MS = 15000;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, REFRESH_MS);
    // Ao voltar o foco para a aba, atualiza imediatamente.
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apontamentos de horas, checklist e aprovações (somente staff).
  const loadTime = () => {
    if (!id) return;
    api.get<TimeEntry[]>(`/tickets/${id}/time`).then(setTimeEntries).catch(() => setTimeEntries([]));
  };
  const loadChecklist = () => {
    if (!id) return;
    api.get<ChecklistItem[]>(`/tickets/${id}/checklist`).then(setChecklist).catch(() => setChecklist([]));
  };
  const loadApprovals = () => {
    if (!id) return;
    api.get<Approval[]>(`/tickets/${id}/approvals`).then(setApprovals).catch(() => setApprovals([]));
  };
  const loadVisits = () => {
    if (!id) return;
    api.get<Visit[]>(`/tickets/${id}/visits`).then(setVisits).catch(() => setVisits([]));
  };
  const loadSurvey = () => {
    if (!id) return;
    api.get<{ rating?: number; comment?: string; answeredAt?: string }[]>(`/tickets/${id}/surveys`)
      .then((list) => setSurvey(list.find((s) => s.answeredAt) ?? list[0] ?? null))
      .catch(() => setSurvey(null));
  };
  useEffect(() => { if (isStaff) { loadTime(); loadChecklist(); loadApprovals(); loadVisits(); loadSurvey(); } }, [id, isStaff]);

  // Departamentos: carregados para todos os papéis (inclusive cliente) para exibir
  // no chamado para qual departamento ele foi aberto. A rota é acessível a qualquer
  // usuário autenticado.
  useEffect(() => {
    api.get<Paginated<Department>>('/departments').then((r) => setDepartments(r.items)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isStaff) return;
    api.get<Paginated<User>>('/agents').then((r) => setAgents(r.items)).catch(() => {});
    api.get<Paginated<Category>>('/categories').then((r) => setCategories(r.items)).catch(() => {});
    api.get<Paginated<Asset>>('/assets').then((r) => setAssets(r.items)).catch(() => {});
    api.get<Paginated<Company>>('/companies').then((r) => setCompanies(r.items)).catch(() => {});
    api.get<Paginated<{ id: string; title: string; body: string }>>('/canned').then((r) => setCanned(r.items)).catch(() => {});
    api.get<{ items: { id: string; title: string }[] }>('/kb').then((r) => setArticles(r.items)).catch(() => {});
  }, [isStaff]);

  // Status que exigem diagnóstico (causa/solução) antes de aplicar.
  const [closingStatus, setClosingStatus] = useState<TicketStatus | null>(null);

  const patch = async (data: Record<string, unknown>, msg: string) => {
    if (!id) return;
    try {
      await api.patch(`/tickets/${id}`, data);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao atualizar');
    }
  };

  // Intercepta mudanças de status: resolver/fechar abrem o popup de encerramento
  // (causa opcional + solução obrigatória). Os demais aplicam direto.
  const requestStatusChange = (next: TicketStatus) => {
    if (next === detail?.ticket.status) return;
    if (next === TicketStatus.RESOLVED || next === TicketStatus.CLOSED) {
      setClosingStatus(next);
      return;
    }
    patch({ status: next }, 'Status atualizado');
  };

  // Exclui o chamado em definitivo (admin) e volta para a lista de chamados.
  const doDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await api.delete(`/tickets/${id}`);
      toast.success('Chamado excluído');
      navigate('/tickets');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao excluir');
      setDeleting(false);
    }
  };

  // Executa uma ação POST/DELETE de estrutura do chamado e recarrega.
  const runAction = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha na operação');
    }
  };

  const onPickFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      const uploaded = await Promise.all(Array.from(files).map(uploadFile));
      setAttachments((a) => [...a, ...uploaded]);
      toast.success(`${uploaded.length} anexo(s) adicionado(s)`);
    } catch {
      toast.error('Falha ao enviar anexo');
    }
  };

  // Considera vazio quando não há texto nem imagem no HTML.
  const isEmptyHtml = (html: string) => !html.replace(/<[^>]*>/g, '').trim() && !/<img/i.test(html);

  // Envia a resposta. Reutilizável pelo submit do form e pelo atalho Ctrl+Enter.
  const submitReply = async () => {
    if (isEmptyHtml(reply) || !id || sending) return;
    setSending(true);
    try {
      await api.post(`/tickets/${id}/messages`, {
        body: reply,
        direction: internal ? MessageDirection.INTERNAL : undefined,
        attachments: attachments.length ? attachments : undefined,
      });
      setReply(''); setAttachments([]); setInternal(false);
      toast.success('Mensagem enviada');
      load();
    } catch (e2) {
      toast.error(e2 instanceof Error ? e2.message : 'Falha ao enviar');
    } finally {
      setSending(false);
    }
  };

  const sendReply = (e: FormEvent) => {
    e.preventDefault();
    void submitReply();
  };

  if (loading) return <div className="flex justify-center" style={{ padding: 48 }}><span className="spinner" /></div>;
  if (!detail) return <div className="card empty"><div className="empty-icon">🔍</div>Chamado não encontrado.</div>;

  const { ticket, messages } = detail;

  return (
    <div className="full-bleed">
      <Link to="/tickets" className="text-sm">← Voltar aos chamados</Link>

      <div className="page-2col mt-2">
        {/* Coluna principal */}
        <div>
          <div className="card">
            <div className="text-dim text-sm">#{ticket.number} · {ticket.channel}</div>
            <h1 className="mb-0">{ticket.subject}</h1>
          </div>

          {/* Responder — fica no topo (logo após o assunto) para não "descer"
              quando a thread do chamado é longa. */}
          <form onSubmit={sendReply} className="card mt-4">
            <div className="flex items-center justify-between mb-4">
              <label className="label mb-0">Responder</label>
              {isStaff && (
                <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
                  Nota interna (cliente não vê)
                </label>
              )}
            </div>
            <RichEditor
              value={reply}
              onChange={setReply}
              onSubmit={submitReply}
              placeholder={internal ? 'Anotação interna... (Ctrl+Enter envia)' : 'Escreva sua resposta. Ctrl+Enter envia. Cole imagens se precisar.'}
              minHeight={110}
            />
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {attachments.map((a) => (
                  <span key={a.id} className="badge badge-normal">
                    📎 {a.fileName}
                    <span style={{ cursor: 'pointer' }} onClick={() => setAttachments((list) => list.filter((x) => x.id !== a.id))}> ✕</span>
                  </span>
                ))}
              </div>
            )}
            <div className="flex justify-between items-center mt-3">
              <div className="flex gap-2 items-center">
                <input ref={fileRef} type="file" multiple hidden onChange={(e) => onPickFiles(e.target.files)} />
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => fileRef.current?.click()}>📎 Anexar</button>
                {isStaff && canned.length > 0 && (
                  <select
                    className="select"
                    style={{ width: 200 }}
                    value=""
                    onChange={(e) => {
                      const c = canned.find((x) => x.id === e.target.value);
                      if (c) setReply((r) => r + `<p>${c.body.replace(/\n/g, '<br/>')}</p>`);
                    }}
                  >
                    <option value="">💬 Resposta pronta...</option>
                    {canned.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                )}
              </div>
              <button className="btn btn-primary" type="submit" disabled={sending || isEmptyHtml(reply)}>
                {sending ? 'Enviando...' : internal ? 'Salvar nota' : 'Enviar resposta'}
              </button>
            </div>
          </form>

          {/* Thread: mensagem mais recente primeiro. A primeira (mais recente)
              já vem expandida; as demais longas ficam recolhidas com "Ler mais". */}
          <div className="flex flex-col gap-3 mt-4">
            {[...messages].reverse().map((m, i) => (
              <MessageBubble key={m.id} message={m} defaultExpanded={i === 0} />
            ))}
          </div>
        </div>

        {/* Painel lateral */}
        <aside className="flex flex-col gap-4">
          <div className="card">
            <h3>Detalhes</h3>
            <div className="flex flex-col gap-3 text-sm">
              <InfoRow label="Status"><StatusBadge status={ticket.status} /></InfoRow>
              <InfoRow label="Prioridade"><PriorityBadge priority={ticket.priority} /></InfoRow>
              {ticket.departmentId && (
                <InfoRow label="Departamento">
                  <span className="badge badge-normal">🏢 {departments.find((d) => d.id === ticket.departmentId)?.name ?? '—'}</span>
                </InfoRow>
              )}
              {ticket.categoryId && <InfoRow label="Categoria"><span className="text-muted">{categoryPath(categories, ticket.categoryId) ?? '—'}</span></InfoRow>}
              {ticket.impact && <InfoRow label="Impacto"><span className="text-muted">{ImpactLabels[ticket.impact]}</span></InfoRow>}
              {ticket.urgency && <InfoRow label="Urgência"><span className="text-muted">{UrgencyLabels[ticket.urgency]}</span></InfoRow>}
              <InfoRow label="Solicitante"><span>{ticket.requesterName ?? '—'}</span></InfoRow>
              {ticket.requesterContact && (
                <InfoRow label="Contato">
                  <span className="flex items-center gap-2" style={{ justifyContent: 'flex-end' }}>
                    <span className="text-muted">{ticket.requesterContact}</span>
                    {/* Só oferece ligar quando o contato é um telefone (não e-mail nem grupo). */}
                    {isStaff && !ticket.requesterContact.includes('@') && (
                      <CallButton number={ticket.requesterContact} ticketId={ticket.id} contactName={ticket.requesterName} />
                    )}
                  </span>
                </InfoRow>
              )}
              <InfoRow label="Aberto em"><span className="text-muted">{new Date(ticket.createdAt).toLocaleString('pt-BR')}</span></InfoRow>
              {ticket.scheduledAt && <InfoRow label="Agendado"><span className="text-muted">{new Date(ticket.scheduledAt).toLocaleString('pt-BR')}</span></InfoRow>}
              {ticket.firstResponseDueAt && (
                <InfoRow label="SLA 1ª resposta"><SlaBadge dueAt={ticket.firstResponseDueAt} doneAt={ticket.firstRespondedAt} breached={ticket.breachedFirstResponse} paused={ticket.slaPaused} /></InfoRow>
              )}
              {(ticket.resolutionDueAt || ticket.dueAt) && (
                <InfoRow label="SLA resolução"><SlaBadge dueAt={ticket.resolutionDueAt ?? ticket.dueAt} doneAt={ticket.resolvedAt} breached={ticket.breachedResolution} paused={ticket.slaPaused} /></InfoRow>
              )}
              {/* Tempo de execução (cronômetro): total acumulado em HH:MM. Ciclo aberto
                  mostra "(em andamento)"; chamados antigos (sem a métrica) exibem "—". */}
              <InfoRow label="Tempo de execução">
                <span className="text-muted">
                  {ticket.executionSeconds != null ? fmtHM(ticket.executionSeconds) : '—'}
                  {ticket.workStartedAt && <span className="text-dim text-xs"> (em andamento)</span>}
                </span>
              </InfoRow>
              {ticket.reopenedCount ? <InfoRow label="Reaberturas"><span className="text-muted">{ticket.reopenedCount}</span></InfoRow> : null}
              {survey?.rating ? <InfoRow label="Satisfação"><span title={survey.comment ?? ''}>{'⭐'.repeat(survey.rating)} <span className="text-dim text-xs">({survey.rating}/5)</span></span></InfoRow> : null}
              {ticket.parentId && <InfoRow label="Chamado pai"><Link to={`/tickets/${ticket.parentId}`}>ver pai</Link></InfoRow>}
              {ticket.mergedIntoId && <InfoRow label="Mesclado em"><Link to={`/tickets/${ticket.mergedIntoId}`}>ver destino</Link></InfoRow>}
            </div>
            {(() => {
              // Reabertura: staff sem prazo; cliente dono só dentro de 3 dias do
              // fechamento. O backend reforça dono + prazo (aqui é só a UI).
              const closedOrResolved =
                ticket.status === TicketStatus.RESOLVED || ticket.status === TicketStatus.CLOSED;
              if (!closedOrResolved) return null;
              const reopenBase = ticket.closedAt ?? ticket.resolvedAt;
              const windowOpen =
                !!reopenBase && Date.now() - Date.parse(reopenBase) <= 3 * 24 * 60 * 60 * 1000;
              const canReopen = isStaff || windowOpen;
              if (canReopen) {
                return (
                  <button className="btn btn-sm w-full mt-3" onClick={() => runAction(() => api.post(`/tickets/${id}/reopen`), 'Chamado reaberto')}>
                    ↩ Reabrir chamado
                  </button>
                );
              }
              // Cliente fora do prazo: aviso discreto no lugar do botão.
              return (
                <div className="text-muted text-xs mt-3">
                  Prazo de reabertura expirado — abra um novo chamado.
                </div>
              );
            })()}
            {isStaff && (
              <button className="btn btn-sm w-full mt-2" onClick={() => setAddUserOpen(true)}>
                ＋ Adicionar usuário
              </button>
            )}
          </div>

          {isStaff && (
            <div className="card">
              <h3>Ações</h3>
              {/* Atalho: inicia o atendimento (status -> Em atendimento) num clique. */}
              {ticket.status !== TicketStatus.IN_PROGRESS &&
                ticket.status !== TicketStatus.RESOLVED &&
                ticket.status !== TicketStatus.CLOSED && (
                <button
                  className="btn btn-primary w-full mb-3"
                  title="Muda o status para Em atendimento e atribui o chamado a você (se ainda não tiver responsável)"
                  onClick={() => patch(
                    // Inicia o atendimento e, se o chamado ainda não tem responsável,
                    // atribui a você no mesmo passo.
                    ticket.assigneeId
                      ? { status: TicketStatus.IN_PROGRESS }
                      : { status: TicketStatus.IN_PROGRESS, assigneeId: user?.id },
                    ticket.assigneeId ? 'Atendimento iniciado' : 'Atendimento iniciado e atribuído a você',
                  )}
                >
                  ▶ Iniciar atendimento
                </button>
              )}
              {/* Cronômetro: Pausa Atendente. Mostra Pausar quando há ciclo em andamento e
                  não está pausado manualmente; Retomar quando a pausa manual está ativa. */}
              {ticket.execManualPaused ? (
                <button
                  className="btn btn-sm w-full mb-3"
                  title="Retoma a contagem do tempo de execução"
                  onClick={() => runAction(() => api.post(`/tickets/${id}/timer/resume`), 'Cronômetro retomado')}
                >
                  ▶ Retomar cronômetro
                </button>
              ) : ticket.workStartedAt ? (
                <button
                  className="btn btn-sm btn-ghost w-full mb-3"
                  title="Pausa a contagem do tempo de execução (Pausa Atendente)"
                  onClick={() => runAction(() => api.post(`/tickets/${id}/timer/pause`), 'Cronômetro pausado')}
                >
                  ⏸ Pausar cronômetro
                </button>
              ) : null}
              {ticket.execManualPaused && (
                <p className="text-dim text-xs mb-3" style={{ marginTop: '-0.5rem' }}>⏸ Cronômetro pausado (Pausa Atendente)</p>
              )}
              {/* Caixa de status destacada: chama atenção por ser a ação central do fluxo. */}
              <div
                className="field"
                style={{
                  background: 'var(--brand-soft)',
                  border: '1px solid var(--brand-ring)',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--s3)',
                }}
              >
                <label className="label" style={{ color: 'var(--brand)', fontWeight: 700 }}>Status do Chamado</label>
                <select
                  className="select"
                  value={ticket.status}
                  onChange={(e) => requestStatusChange(e.target.value as TicketStatus)}
                >
                  {statusOptions.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
                <p className="text-dim text-xs mt-1" style={{ marginBottom: 0 }}>
                  Resolver ou fechar exige registrar a solução.
                </p>
              </div>
              <div className="field">
                <label className="label">Prioridade</label>
                <select className="select" value={ticket.priority} onChange={(e) => patch({ priority: e.target.value }, 'Prioridade atualizada')}>
                  <option value={TicketPriority.LOW}>Baixa</option>
                  <option value={TicketPriority.NORMAL}>Normal</option>
                  <option value={TicketPriority.HIGH}>Alta</option>
                  <option value={TicketPriority.URGENT}>Urgente</option>
                </select>
                {ticket.impact && ticket.urgency && <p className="text-dim text-xs mt-1">Alterar impacto/urgência recalcula a prioridade.</p>}
              </div>
              {buildCategoryOptions(categories).length > 0 && (
                <div className="field">
                  <label className="label">Categoria</label>
                  <select className="select" value={ticket.categoryId ?? ''} onChange={(e) => patch({ categoryId: e.target.value || undefined }, 'Categoria atualizada')}>
                    <option value="">—</option>
                    {buildCategoryOptions(categories).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
              )}
              <div className="flex gap-3">
                <div className="field flex-1">
                  <label className="label">Impacto</label>
                  <select className="select" value={ticket.impact ?? ''} onChange={(e) => patch({ impact: e.target.value || undefined }, 'Impacto atualizado')}>
                    <option value="">—</option>
                    <option value={Impact.LOW}>Baixo</option>
                    <option value={Impact.MEDIUM}>Médio</option>
                    <option value={Impact.HIGH}>Alto</option>
                  </select>
                </div>
                <div className="field flex-1">
                  <label className="label">Urgência</label>
                  <select className="select" value={ticket.urgency ?? ''} onChange={(e) => patch({ urgency: e.target.value || undefined }, 'Urgência atualizada')}>
                    <option value="">—</option>
                    <option value={Urgency.LOW}>Baixa</option>
                    <option value={Urgency.MEDIUM}>Média</option>
                    <option value={Urgency.HIGH}>Alta</option>
                  </select>
                </div>
              </div>
              <div className="field">
                <label className="label">Empresa</label>
                <select className="select" value={ticket.companyId ?? ''} onChange={(e) => patch({ companyId: e.target.value || undefined }, 'Empresa atualizada')}>
                  <option value="">— Sem empresa —</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div className="text-dim text-xs mt-1">
                  Definida automaticamente pelo domínio do e-mail ou pelo cliente cadastrado; ajuste aqui se necessário.
                </div>
              </div>
              <div className="field">
                <label className="label">Departamento</label>
                <select className="select" value={ticket.departmentId ?? ''} onChange={(e) => patch({ departmentId: e.target.value || undefined }, 'Departamento atualizado')}>
                  <option value="">—</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div className="field mb-0">
                <label className="label">Responsável</label>
                <select className="select" value={ticket.assigneeId ?? ''} onChange={(e) => patch({ assigneeId: e.target.value || undefined }, 'Responsável atualizado')}>
                  <option value="">Não atribuído</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              {ticket.assigneeId !== user?.id && (
                <button className="btn btn-sm w-full mt-3" onClick={() => patch({ assigneeId: user?.id }, 'Atribuído a você')}>
                  Atribuir a mim
                </button>
              )}
              {/* Exclusão do chamado: exclusiva do admin, abaixo das demais ações. */}
              {isAdmin && (
                <button
                  className="btn btn-danger w-full mt-3"
                  title="Excluir este chamado e todo o histórico (irreversível)"
                  onClick={() => setConfirmingDelete(true)}
                >
                  🗑 Excluir chamado
                </button>
              )}
            </div>
          )}

          {isStaff && (
            <Section title="Checklist" defaultOpen>
              <ChecklistPanel
                items={checklist}
                onAdd={async (text, parentId) => { await runAction(() => api.post(`/tickets/${id}/checklist`, { text, parentId }), 'Item adicionado'); loadChecklist(); }}
                onToggle={async (it) => { await runAction(() => api.patch(`/tickets/${id}/checklist/${it.order}/${it.id}`, { done: !it.done }), 'Checklist atualizado'); loadChecklist(); }}
                onRemove={async (it) => { await runAction(() => api.delete(`/tickets/${id}/checklist/${it.order}/${it.id}`), 'Item removido'); loadChecklist(); }}
              />
            </Section>
          )}

          {isStaff && (
            <Section title="Tags">
              <TagEditor
                tags={ticket.tags ?? []}
                onChange={(tags) => patch({ tags }, 'Tags atualizadas')}
              />
            </Section>
          )}

          {isStaff && (
            <Section title="Diagnóstico">
              <DiagnosisEditor
                cause={ticket.cause}
                solution={ticket.solution}
                rootCause={ticket.rootCause}
                onSave={(data) => patch(data, 'Diagnóstico salvo')}
              />
            </Section>
          )}

          {isStaff && (
            <Section title="Aprovações">
              <ApprovalsPanel
                approvals={approvals}
                agents={agents}
                onRequest={async (subject, approverId) => { await runAction(() => api.post(`/tickets/${id}/approvals`, { subject, approverId }), 'Aprovação solicitada'); loadApprovals(); }}
                onDecide={async (a, approved) => { await runAction(() => api.patch(`/tickets/${id}/approvals/${encodeURIComponent(a.createdAt)}/${a.id}`, { approved }), 'Aprovação decidida'); loadApprovals(); }}
              />
            </Section>
          )}

          {isStaff && (
            <Section title="Visitas técnicas">
              <VisitsPanel
                visits={visits}
                agents={agents}
                onSchedule={async (payload) => { await runAction(() => api.post(`/tickets/${id}/visits`, payload), 'Visita agendada'); loadVisits(); }}
                onUpdate={async (v, patch) => { await runAction(() => api.patch(`/tickets/${id}/visits/${encodeURIComponent(v.scheduledAt)}/${v.id}`, patch), 'Visita atualizada'); loadVisits(); }}
              />
            </Section>
          )}

          {isStaff && ticket.companyId && (() => {
            // Dados cadastrais da empresa do chamado, para consulta rápida do técnico.
            const company = companies.find((c) => c.id === ticket.companyId);
            if (!company) return null;
            const address = [company.street, company.number, company.neighborhood, company.city, company.state]
              .filter(Boolean).join(', ');
            // Telefone e ramal interno são renderizados à parte (com botão de ligar).
            return (
              <Section title="Empresa" defaultOpen>
                <div className="flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-dim text-sm">Empresa</span>
                    <span className="text-sm" style={{ textAlign: 'right', wordBreak: 'break-word' }}>{company.name}</span>
                  </div>
                  {company.document && (
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-dim text-sm">CNPJ/CPF</span>
                      <span className="text-sm" style={{ textAlign: 'right', wordBreak: 'break-word' }}>{company.document}</span>
                    </div>
                  )}
                  {/* Telefone com botão de ligar (registra a ligação no chamado). */}
                  {company.phone && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-dim text-sm">Telefone</span>
                      <span className="flex items-center gap-2 text-sm">
                        {company.phone}
                        <CallButton number={company.phone} ticketId={ticket.id} contactName={company.name} />
                      </span>
                    </div>
                  )}
                  {/* Ramal interno no nosso 3CX (quando o cliente usa nosso PBX). */}
                  {company.internalExtension && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-dim text-sm">Ramal interno</span>
                      <span className="flex items-center gap-2 text-sm">
                        {company.internalExtension}
                        <CallButton number={company.internalExtension} ticketId={ticket.id} contactName={company.name} title={`Ligar para o ramal ${company.internalExtension}`} />
                      </span>
                    </div>
                  )}
                  {company.email && (
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-dim text-sm">E-mail</span>
                      <span className="text-sm" style={{ textAlign: 'right', wordBreak: 'break-word' }}>{company.email}</span>
                    </div>
                  )}
                  {[
                    { label: 'Endereço', value: address || undefined },
                    { label: 'CEP', value: company.cep },
                    { label: 'Domínios', value: company.emailDomains?.join(', ') },
                  ].filter((r) => r.value).map((r) => (
                    <div key={r.label} className="flex items-start justify-between gap-3">
                      <span className="text-dim text-sm">{r.label}</span>
                      <span className="text-sm" style={{ textAlign: 'right', wordBreak: 'break-word' }}>{r.value}</span>
                    </div>
                  ))}
                  <Link to={`/admin`} className="text-sm mt-2">Abrir em Administração ↗</Link>
                </div>
              </Section>
            );
          })()}

          {isStaff && (
            <Section title="Ativo (CMDB)">
              {(() => {
                // Ativos da empresa do chamado (ou todos, se o chamado não tem empresa).
                const scoped = assets.filter((a) => !ticket.companyId || a.companyId === ticket.companyId);
                const current = assets.find((a) => a.id === ticket.assetId);
                return (
                  <>
                    {current && (
                      <div className="text-sm mb-2">
                        <strong>{current.name}</strong> · {AssetTypeLabels[current.type]}
                        {current.ipAddress && <span className="text-dim"> · {current.ipAddress}</span>}
                      </div>
                    )}
                    <select
                      className="select"
                      value={ticket.assetId ?? ''}
                      onChange={(e) => patch({ assetId: e.target.value || undefined }, 'Ativo vinculado')}
                    >
                      <option value="">— Nenhum —</option>
                      {scoped.map((a) => <option key={a.id} value={a.id}>{a.name}{a.identifier ? ` (${a.identifier})` : ''}</option>)}
                    </select>
                    {scoped.length === 0 && <p className="text-dim text-xs mt-1">Nenhum ativo cadastrado para esta empresa.</p>}
                  </>
                );
              })()}
            </Section>
          )}

          {isStaff && (
            <Section title="Horas">
              <TimeLogger
                entries={timeEntries}
                hasCompany={!!ticket.companyId}
                onAdd={async (payload) => {
                  await runAction(() => api.post(`/tickets/${id}/time`, payload), 'Horas apontadas');
                  loadTime();
                }}
                onRemove={async (e) => {
                  await runAction(
                    () => api.delete(`/tickets/${id}/time/${encodeURIComponent(e.performedAt)}/${e.id}`),
                    'Apontamento removido',
                  );
                  loadTime();
                }}
              />
            </Section>
          )}

          {isStaff && (
            <Section title="Seguidores">
              <div className="flex flex-col gap-2">
                {(ticket.followerIds ?? []).map((fid) => {
                  const ag = agents.find((a) => a.id === fid);
                  return (
                    <div key={fid} className="flex items-center justify-between text-sm">
                      <span>👤 {ag?.name ?? fid}</span>
                      <span style={{ cursor: 'pointer' }} onClick={() => runAction(() => api.delete(`/tickets/${id}/followers/${fid}`), 'Seguidor removido')}>✕</span>
                    </div>
                  );
                })}
                {(ticket.followerIds ?? []).length === 0 && <span className="text-dim text-sm">Nenhum seguidor.</span>}
              </div>
              <select
                className="select mt-2"
                value=""
                onChange={(e) => {
                  const uid = e.target.value;
                  if (uid) runAction(() => api.post(`/tickets/${id}/followers`, { userId: uid }), 'Seguidor adicionado');
                }}
              >
                <option value="">Adicionar seguidor...</option>
                {agents.filter((a) => !(ticket.followerIds ?? []).includes(a.id)).map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </Section>
          )}

          {isStaff && (
            <Section title="Relacionar / Mesclar">
              <MergePanel
                currentTicketId={ticket.id}
                currentNumber={ticket.number}
                onMerge={(ids) => runAction(async () => {
                  // Ignora seleção vazia; mescla pelos ids reais já conhecidos.
                  if (ids.length === 0) return;
                  await api.post(`/tickets/${id}/merge`, { sourceTicketIds: ids });
                }, 'Chamados mesclados')}
              />
            </Section>
          )}

          {isStaff && (
            <Section title="Base de conhecimento">
              <div className="flex flex-col gap-2">
                {(ticket.linkedArticleIds ?? []).map((aid) => {
                  const art = articles.find((a) => a.id === aid);
                  return (
                    <div key={aid} className="flex items-center justify-between text-sm">
                      <span>📄 {art?.title ?? aid}</span>
                      <span style={{ cursor: 'pointer' }} onClick={() => patch({ linkedArticleIds: (ticket.linkedArticleIds ?? []).filter((x) => x !== aid) }, 'Artigo desvinculado')}>✕</span>
                    </div>
                  );
                })}
              </div>
              <select
                className="select mt-2"
                value=""
                onChange={(e) => {
                  const aid = e.target.value;
                  if (aid && !(ticket.linkedArticleIds ?? []).includes(aid)) {
                    patch({ linkedArticleIds: [...(ticket.linkedArticleIds ?? []), aid] }, 'Artigo vinculado');
                  }
                }}
              >
                <option value="">Vincular artigo...</option>
                {articles.filter((a) => !(ticket.linkedArticleIds ?? []).includes(a.id)).map((a) => (
                  <option key={a.id} value={a.id}>{a.title}</option>
                ))}
              </select>
            </Section>
          )}

          {isStaff && (
            <Section title="Tarefa">
              <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={!!ticket.isTask} onChange={(e) => patch({ isTask: e.target.checked }, 'Marcado como tarefa')} />
                Tratar como tarefa (alerta o responsável)
              </label>
            </Section>
          )}

          {ticket.sourceMetadata && Object.keys(ticket.sourceMetadata).length > 0 && (
            <div className="card">
              <h3>Origem</h3>
              <SourceMetadata data={ticket.sourceMetadata} />
            </div>
          )}

          <div className="card">
            <h3>Histórico</h3>
            {timeline.length === 0 ? (
              <span className="text-dim text-sm">Sem eventos registrados.</span>
            ) : (
              <div className="timeline">
                {timeline.map((ev) => (
                  <div key={ev.id} className="timeline-item">
                    <span className="timeline-icon">{TicketEventIcons[ev.type] ?? '•'}</span>
                    <div className="timeline-body">
                      <div className="text-sm">
                        <strong>{TicketEventLabels[ev.type] ?? ev.type}</strong>
                        {ev.type === 'status_changed' && ev.to && <span className="text-muted"> → {ev.to}</span>}
                        {ev.note && <span className="text-muted"> · {ev.note}</span>}
                      </div>
                      <div className="text-dim text-xs">
                        {new Date(ev.at).toLocaleString('pt-BR')}{ev.actorName ? ` · ${ev.actorName}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Popup de encerramento: exige a solução (e opcionalmente a causa) para resolver/fechar. */}
      {closingStatus && (
        <CloseTicketModal
          target={closingStatus}
          initialCause={ticket.cause}
          initialSolution={ticket.solution}
          onCancel={() => setClosingStatus(null)}
          onConfirm={async (data) => {
            await patch(
              { status: data.status, cause: data.cause, solution: data.solution },
              data.status === TicketStatus.CLOSED ? 'Chamado fechado' : 'Chamado resolvido',
            );
            setClosingStatus(null);
            // Após resolver/fechar, volta para a lista de chamados.
            navigate('/tickets');
          }}
        />
      )}

      {/* Adicionar usuário: cria um acesso de cliente vinculado a uma empresa. */}
      {isStaff && (
        <AddUserModal
          open={addUserOpen}
          companies={companies}
          defaultName={ticket.requesterName ?? ''}
          defaultEmail={ticket.requesterContact && ticket.requesterContact.includes('@') ? ticket.requesterContact : ''}
          defaultPhone={ticket.requesterContact && !ticket.requesterContact.includes('@') ? ticket.requesterContact : ''}
          defaultCompanyId={ticket.companyId ?? ''}
          onClose={() => setAddUserOpen(false)}
          onCreated={() => { setAddUserOpen(false); toast.success('Usuário criado. Um convite de acesso foi enviado por e-mail.'); }}
        />
      )}

      {/* Confirmação de exclusão do chamado (admin). */}
      {isAdmin && (
        <Modal
          open={confirmingDelete}
          title={`Excluir chamado #${ticket.number}`}
          onClose={() => { if (!deleting) setConfirmingDelete(false); }}
          maxWidth={480}
          footer={
            <>
              <button className="btn btn-ghost" type="button" onClick={() => setConfirmingDelete(false)} disabled={deleting}>Cancelar</button>
              <button className="btn btn-danger" type="button" onClick={doDelete} disabled={deleting}>
                {deleting ? 'Excluindo...' : 'Excluir chamado'}
              </button>
            </>
          }
        >
          <p className="text-muted text-sm" style={{ marginTop: 0 }}>
            Esta ação apaga o chamado <strong>#{ticket.number} — {ticket.subject}</strong> e <strong>todo o histórico</strong>
            {' '}(mensagens, eventos, apontamentos, anexos). Não é possível desfazer.
          </p>
        </Modal>
      )}
    </div>
  );
}

// Modal para criar um acesso de cliente vinculado a uma empresa, direto do chamado.
// Reaproveita POST /users (role=client). Sem senha => o Cognito envia o convite por
// e-mail. Pré-preenche com os dados do solicitante do chamado.
function AddUserModal({
  open,
  companies,
  defaultName,
  defaultEmail,
  defaultPhone,
  defaultCompanyId,
  onClose,
  onCreated,
}: {
  open: boolean;
  companies: Company[];
  defaultName: string;
  defaultEmail: string;
  defaultPhone: string;
  defaultCompanyId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);
  const [companyId, setCompanyId] = useState(defaultCompanyId);
  const [saving, setSaving] = useState(false);

  // Sincroniza os campos quando o modal (re)abre com dados do chamado atual.
  useEffect(() => {
    if (open) {
      setName(defaultName); setEmail(defaultEmail); setPhone(defaultPhone); setCompanyId(defaultCompanyId);
    }
  }, [open, defaultName, defaultEmail, defaultPhone, defaultCompanyId]);

  const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Informe o nome.');
    if (!isEmail(email)) return toast.error('Informe um e-mail válido.');
    if (!companyId) return toast.error('Selecione a empresa.');
    setSaving(true);
    try {
      await api.post('/users', {
        name: name.trim(),
        email: email.trim(),
        role: UserRole.CLIENT,
        companyId,
        phone: phone.replace(/\D/g, '') || undefined,
      });
      onCreated();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao criar usuário');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Adicionar usuário"
      onClose={onClose}
      maxWidth={480}
      footer={
        <>
          <button className="btn btn-ghost" type="button" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn btn-primary" type="submit" form="add-user-form" disabled={saving}>
            {saving ? 'Criando...' : 'Criar acesso'}
          </button>
        </>
      }
    >
      <form id="add-user-form" onSubmit={submit}>
        <div className="field"><label className="label">Nome</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </div>
        <div className="field"><label className="label">E-mail</label>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field"><label className="label">Telefone (opcional)</label>
          <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(11) 99999-9999" inputMode="numeric" />
        </div>
        <div className="field mb-0"><label className="label">Empresa</label>
          <select className="select" value={companyId} onChange={(e) => setCompanyId(e.target.value)} required>
            <option value="">Selecione a empresa</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <p className="text-dim text-xs mt-1">O usuário receberá um convite de acesso por e-mail (senha temporária).</p>
        </div>
      </form>
    </Modal>
  );
}

// Popup de encerramento do chamado: coleta Causa (opcional) e Solução (obrigatória)
// com editor rico (colar imagem / anexar). Permite ajustar o status alvo (resolver/fechar)
// e só habilita confirmar quando há solução preenchida.
function CloseTicketModal({
  target,
  initialCause,
  initialSolution,
  onConfirm,
  onCancel,
}: {
  target: TicketStatus;
  initialCause?: string;
  initialSolution?: string;
  onConfirm: (data: { status: TicketStatus; cause: string; solution: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [status, setStatus] = useState<TicketStatus>(target);
  const [cause, setCause] = useState(initialCause ?? '');
  const [solution, setSolution] = useState(initialSolution ?? '');
  const [saving, setSaving] = useState(false);

  // Considera vazio quando não há texto nem imagem no HTML.
  const isEmptyHtml = (html: string) => !html.replace(/<[^>]*>/g, '').trim() && !/<img/i.test(html);
  const solutionMissing = isEmptyHtml(solution);

  const confirm = async () => {
    if (solutionMissing || saving) return;
    setSaving(true);
    try {
      await onConfirm({ status, cause, solution });
    } finally {
      setSaving(false);
    }
  };

  const title = status === TicketStatus.CLOSED ? 'Fechar chamado' : 'Resolver chamado';

  return (
    <Modal
      open
      title={title}
      onClose={onCancel}
      maxWidth={640}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onCancel} disabled={saving}>Cancelar</button>
          <button
            className="btn btn-primary"
            onClick={confirm}
            disabled={solutionMissing || saving}
            title={solutionMissing ? 'Preencha a solução para continuar' : undefined}
          >
            {saving ? 'Salvando...' : status === TicketStatus.CLOSED ? 'Fechar chamado' : 'Resolver chamado'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="field mb-0">
          <label className="label">Status do Chamado</label>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as TicketStatus)}>
            <option value={TicketStatus.RESOLVED}>Resolvido</option>
            <option value={TicketStatus.CLOSED}>Fechado</option>
          </select>
        </div>

        <div className="field mb-0">
          <label className="label">Causa <span className="text-dim">(opcional)</span></label>
          <RichEditor value={cause} onChange={setCause} placeholder="O que originou o problema" minHeight={100} />
        </div>

        <div className="field mb-0">
          <label className="label">
            Solução <span style={{ color: 'var(--danger)' }}>*</span>
          </label>
          <RichEditor value={solution} onChange={setSolution} placeholder="O que foi feito para resolver. Cole imagens ou anexe evidências." minHeight={140} />
          {solutionMissing && <p className="text-xs mt-1" style={{ color: 'var(--danger)', marginBottom: 0 }}>A solução é obrigatória para resolver ou fechar o chamado.</p>}
        </div>
      </div>
    </Modal>
  );
}

// Seção lateral recolhível (usa <details> nativo). defaultOpen abre de início.
function Section({ title, children, defaultOpen }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  return (
    <details className="panel-section" open={defaultOpen}>
      <summary>{title}</summary>
      <div className="panel-body">{children}</div>
    </details>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-dim">{label}</span>
      <span style={{ textAlign: 'right' }}>{children}</span>
    </div>
  );
}

// Altura (px) a partir da qual uma mensagem é considerada "longa" e recolhida.
const MSG_COLLAPSED_MAX = 320;

// Monta a URL pública (redireciona para o S3) de um anexo a partir da s3Key.
// Mesmo padrão do RichEditor/WhatsappPage: base64url da chave num único segmento.
function attachmentUrl(s3Key: string): string {
  const encoded = btoa(s3Key).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `${config.apiUrl}/public/attachments/${encoded}`;
}

// Renderiza um anexo conforme o tipo: imagem/vídeo/áudio inline; demais como link.
function MessageAttachment({ att }: { att: Attachment }) {
  const url = attachmentUrl(att.s3Key);
  const type = (att.contentType ?? '').toLowerCase();
  if (type.startsWith('image/')) {
    return <ImageThumb src={url} alt={att.fileName} thumbStyle={{ maxWidth: 'min(320px, 100%)', maxHeight: 320 }} />;
  }
  if (type.startsWith('video/')) {
    return <video src={url} controls style={{ maxWidth: 'min(320px, 100%)', borderRadius: 8, display: 'block' }} />;
  }
  if (type.startsWith('audio/')) {
    return <audio src={url} controls style={{ maxWidth: '100%' }} />;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="badge badge-closed" style={{ textDecoration: 'none' }}>
      📎 {att.fileName}
    </a>
  );
}

// Balão de mensagem da thread. Mensagens longas ficam recolhidas com botão
// "Ler mais"/"Ler menos"; a mais recente entra expandida (defaultExpanded).
function MessageBubble({ message: m, defaultExpanded }: { message: Message; defaultExpanded?: boolean }) {
  const outbound = m.direction === MessageDirection.OUTBOUND;
  const note = m.direction === MessageDirection.INTERNAL;
  const bodyRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(!!defaultExpanded);
  // overflowing = o conteúdo real excede a altura recolhida (só então mostramos "Ler mais").
  const [overflowing, setOverflowing] = useState(false);

  // Mede o conteúdo após renderizar (e quando o corpo muda) para decidir se recolhe.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > MSG_COLLAPSED_MAX + 8);
  }, [m.body]);

  const collapsed = overflowing && !expanded;

  return (
    <div className="flex" style={{ justifyContent: outbound ? 'flex-end' : 'flex-start' }}>
      <div
        style={{
          maxWidth: '80%',
          background: note ? 'var(--warning-soft)' : outbound ? 'var(--brand)' : 'var(--surface)',
          color: outbound && !note ? '#fff' : 'var(--text)',
          border: outbound && !note ? 'none' : '1px solid var(--border)',
          borderRadius: 'var(--radius)', padding: 'var(--s3) var(--s4)', boxShadow: 'var(--shadow-xs)',
        }}
      >
        <div className="flex items-center justify-between gap-4" style={{ marginBottom: 4 }}>
          <strong style={{ fontSize: 13 }}>{m.authorName ?? '—'}{note ? ' · nota interna' : ''}</strong>
          <span style={{ fontSize: 11, opacity: 0.7 }}>{new Date(m.createdAt).toLocaleString('pt-BR')}</span>
        </div>
        <div
          style={{
            position: 'relative',
            maxHeight: collapsed ? MSG_COLLAPSED_MAX : undefined,
            overflow: collapsed ? 'hidden' : undefined,
          }}
        >
          <div ref={bodyRef} className="msg-body" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: m.body }} />
          {/* Gradiente de "esmaecer" indica que há mais conteúdo abaixo. */}
          {collapsed && (
            <div
              aria-hidden
              style={{
                position: 'absolute', left: 0, right: 0, bottom: 0, height: 56,
                background: `linear-gradient(to bottom, transparent, ${note ? 'var(--warning-soft)' : outbound ? 'var(--brand)' : 'var(--surface)'})`,
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
        {m.attachments?.length ? (
          <div className="flex flex-col gap-2 mt-2">
            {m.attachments.map((a) => (
              <MessageAttachment key={a.id} att={a} />
            ))}
          </div>
        ) : null}
        {overflowing && (
          <button
            type="button"
            className="btn btn-sm btn-ghost mt-2"
            style={outbound && !note ? { color: '#fff', borderColor: 'rgba(255,255,255,0.5)' } : undefined}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Ler menos ▲' : 'Ler mais ▼'}
          </button>
        )}
      </div>
    </div>
  );
}

// Origem do chamado (sourceMetadata) em formato legível, em vez de JSON cru.
const SOURCE_LABELS: Record<string, string> = {
  public_portal: 'Portal público',
  mw_scan: 'MW-SCAN (monitoramento)',
  email: 'E-mail',
  whatsapp: 'WhatsApp',
  site_chat: 'Chat do site',
  phone: 'Telefone (3CX)',
  web: 'Portal interno',
};
const META_FIELD_LABELS: Record<string, string> = {
  source: 'Canal de origem',
  serverId: 'ID do servidor',
  serverName: 'Servidor',
  serverIp: 'IP do servidor',
  detectedAt: 'Detectado em',
  metrics: 'Métricas',
};

function formatMetaValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'source' && typeof value === 'string') return SOURCE_LABELS[value] ?? value;
  if (key === 'detectedAt' && typeof value === 'string') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString('pt-BR');
  }
  if (typeof value === 'object') {
    // Objetos aninhados (ex.: métricas) viram "chave: valor" separados por vírgula.
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ');
  }
  return String(value);
}

function SourceMetadata({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return <span className="text-dim text-sm">Sem detalhes de origem.</span>;
  return (
    <div className="flex flex-col gap-2">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-start justify-between gap-3">
          <span className="text-dim text-sm">{META_FIELD_LABELS[key] ?? key}</span>
          <span className="text-sm" style={{ textAlign: 'right', wordBreak: 'break-word' }}>{formatMetaValue(key, value)}</span>
        </div>
      ))}
    </div>
  );
}

// Editor de tags: adiciona por Enter, remove pelo ✕.
function TagEditor({ tags, onChange }: { tags: string[]; onChange: (tags: string[]) => void }) {
  const [input, setInput] = useState('');
  const add = () => {
    const value = input.trim().toLowerCase();
    if (value && !tags.includes(value)) onChange([...tags, value]);
    setInput('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2" style={{ marginBottom: tags.length ? 'var(--s3)' : 0 }}>
        {tags.map((t) => (
          <span key={t} className="badge badge-normal">
            #{t}
            <span style={{ cursor: 'pointer' }} onClick={() => onChange(tags.filter((x) => x !== t))}> ✕</span>
          </span>
        ))}
      </div>
      <input
        className="input"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); add(); }
        }}
        placeholder="Digite e Enter para adicionar"
      />
    </div>
  );
}

// Selo de SLA: mostra cumprido / estourado / pausado / em risco / no prazo.
function SlaBadge({ dueAt, doneAt, breached, paused }: { dueAt?: string; doneAt?: string; breached?: boolean; paused?: boolean }) {
  if (!dueAt) return <span className="text-muted">—</span>;
  const due = new Date(dueAt).getTime();
  const nowMs = Date.now();
  // Já concluído: cumpriu ou estourou conforme a data de conclusão vs prazo.
  if (doneAt) {
    const met = new Date(doneAt).getTime() <= due && !breached;
    return <span className={`badge ${met ? 'badge-resolved' : 'badge-urgent'}`}>{met ? 'Cumprido' : 'Estourado'}</span>;
  }
  if (breached) return <span className="badge badge-urgent">Estourado</span>;
  if (paused) return <span className="badge badge-waiting_client">Pausado</span>;
  const remaining = due - nowMs;
  if (remaining < 0) return <span className="badge badge-urgent">Estourado</span>;
  // Em risco quando falta menos de 1 hora.
  if (remaining < 3600_000) return <span className="badge badge-high">Em risco</span>;
  return <span className="badge badge-resolved">No prazo</span>;
}

// Formata minutos em "Xh Ymin".
function fmtMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${m ? ` ${m}min` : ''}` : `${m}min`;
}

// Formata o tempo de execução (segundos) em "HH:MM" zero-padded (minutos truncados;
// horas sem teto de 2 dígitos). Equivalente ao formatHoursMinutes do backend; mantido
// local para não acoplar frontend↔backend.
function fmtHM(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const totalMinutes = Math.floor(seconds / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Apontamento de horas: formulário + lista + total.
function TimeLogger({
  entries,
  onAdd,
  onRemove,
  hasCompany,
}: {
  entries: TimeEntry[];
  onAdd: (payload: { minutes: number; billable: boolean; description?: string }) => void;
  onRemove: (entry: TimeEntry) => void;
  hasCompany: boolean;
}) {
  const [minutes, setMinutes] = useState('');
  const [billable, setBillable] = useState(true);
  const [description, setDescription] = useState('');

  const total = entries.reduce((s, e) => s + (e.minutes || 0), 0);
  const billableTotal = entries.filter((e) => e.billable).reduce((s, e) => s + (e.minutes || 0), 0);

  const submit = () => {
    const m = Number(minutes);
    if (!(m > 0)) return;
    onAdd({ minutes: m, billable, description: description || undefined });
    setMinutes(''); setDescription(''); setBillable(true);
  };

  return (
    <div>
      {!hasCompany && (
        // Aviso discreto: horas de chamado sem empresa não entram no relatório por empresa.
        <div className="text-sm" style={{ color: 'var(--warning)', marginBottom: 'var(--s3)' }}>
          Este chamado não está vinculado a uma empresa — as horas apontadas NÃO entrarão no relatório de horas de suporte por empresa. Vincule uma empresa no card Ações.
        </div>
      )}
      {entries.length > 0 && (
        <div className="flex flex-col gap-2" style={{ marginBottom: 'var(--s3)' }}>
          {entries.map((e) => (
            <div key={e.id} className="flex items-center justify-between text-sm">
              <span>
                <strong>{fmtMinutes(e.minutes)}</strong>
                {!e.billable && <span className="text-dim"> · não faturável</span>}
                {e.description && <span className="text-muted"> · {e.description}</span>}
                <span className="text-dim text-xs"> · {e.userName ?? '—'}</span>
              </span>
              <span style={{ cursor: 'pointer' }} onClick={() => onRemove(e)}>✕</span>
            </div>
          ))}
          <div className="text-sm" style={{ borderTop: '1px solid var(--border)', paddingTop: 6 }}>
            Total: <strong>{fmtMinutes(total)}</strong>
            {billableTotal !== total && <span className="text-dim"> · faturável {fmtMinutes(billableTotal)}</span>}
          </div>
        </div>
      )}
      <div className="flex gap-2 items-end">
        <div className="field mb-0" style={{ width: 90 }}>
          <label className="label">Minutos</label>
          <input className="input" type="number" min="1" value={minutes} onChange={(ev) => setMinutes(ev.target.value)} />
        </div>
        <label className="flex items-center gap-1 text-sm" style={{ cursor: 'pointer', paddingBottom: 8 }}>
          <input type="checkbox" checked={billable} onChange={(ev) => setBillable(ev.target.checked)} /> faturável
        </label>
      </div>
      <div className="field mt-2 mb-0">
        <input className="input" value={description} onChange={(ev) => setDescription(ev.target.value)} placeholder="Descrição (opcional)" />
      </div>
      <button className="btn btn-sm btn-primary mt-2" onClick={submit} disabled={!(Number(minutes) > 0)}>Apontar horas</button>
    </div>
  );
}

// Checklist do chamado: lista com marcar/remover + campo de adicionar.
function ChecklistPanel({
  items,
  onAdd,
  onToggle,
  onRemove,
}: {
  items: ChecklistItem[];
  onAdd: (text: string, parentId?: string) => void;
  onToggle: (item: ChecklistItem) => void;
  onRemove: (item: ChecklistItem) => void;
}) {
  const [groupText, setGroupText] = useState('');
  // Estado do campo "novo subitem" por item-pai (id do pai → texto).
  const [childText, setChildText] = useState<Record<string, string>>({});

  // Organiza em pais (sem parentId) ordenados, cada um com seus filhos.
  const parents = items.filter((i) => !i.parentId).sort((a, b) => a.order - b.order);
  const childrenOf = (pid: string) => items.filter((i) => i.parentId === pid).sort((a, b) => a.order - b.order);
  // Itens antigos sem hierarquia aparecem como "avulsos" (compatibilidade).
  const done = items.filter((i) => i.done).length;

  const addGroup = () => { if (groupText.trim()) { onAdd(groupText.trim()); setGroupText(''); } };
  const addChild = (pid: string) => {
    const t = (childText[pid] ?? '').trim();
    if (t) { onAdd(t, pid); setChildText((s) => ({ ...s, [pid]: '' })); }
  };

  return (
    <div>
      {items.length > 0 && (
        <div className="text-dim text-xs mb-2">{done}/{items.length} concluídos</div>
      )}

      <div className="flex flex-col gap-3" style={{ marginBottom: 'var(--s3)' }}>
        {parents.map((p) => {
          const kids = childrenOf(p.id);
          const kidsDone = kids.filter((k) => k.done).length;
          return (
            <div key={p.id}>
              {/* Item-pai (categoria) */}
              <div className="flex items-center justify-between text-sm" style={{ fontWeight: 600 }}>
                <label className="flex items-center gap-2" style={{ cursor: 'pointer', flex: 1 }}>
                  <input type="checkbox" checked={p.done} onChange={() => onToggle(p)} />
                  <span style={{ textDecoration: p.done ? 'line-through' : 'none', opacity: p.done ? 0.6 : 1 }}>{p.text}</span>
                  {kids.length > 0 && <span className="text-dim text-xs" style={{ fontWeight: 400 }}>({kidsDone}/{kids.length})</span>}
                </label>
                <span style={{ cursor: 'pointer' }} onClick={() => onRemove(p)} title="Remover categoria e subitens">✕</span>
              </div>
              {/* Subitens */}
              <div className="flex flex-col gap-1" style={{ marginLeft: 22, marginTop: 4, borderLeft: '2px solid var(--border)', paddingLeft: 10 }}>
                {kids.map((k) => (
                  <div key={k.id} className="flex items-center justify-between text-sm">
                    <label className="flex items-center gap-2" style={{ cursor: 'pointer', flex: 1 }}>
                      <input type="checkbox" checked={k.done} onChange={() => onToggle(k)} />
                      <span style={{ textDecoration: k.done ? 'line-through' : 'none', opacity: k.done ? 0.6 : 1 }}>{k.text}</span>
                    </label>
                    <span style={{ cursor: 'pointer' }} onClick={() => onRemove(k)}>✕</span>
                  </div>
                ))}
                {/* Adicionar subitem neste pai */}
                <div className="flex gap-2 mt-1">
                  <input
                    className="input"
                    style={{ fontSize: 12.5, padding: '6px 10px' }}
                    value={childText[p.id] ?? ''}
                    onChange={(e) => setChildText((s) => ({ ...s, [p.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChild(p.id); } }}
                    placeholder="+ subitem"
                  />
                  <button className="btn btn-sm btn-ghost" onClick={() => addChild(p.id)} disabled={!(childText[p.id] ?? '').trim()}>+</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Adicionar nova categoria (item-pai) */}
      <div className="flex gap-2">
        <input className="input" value={groupText} onChange={(e) => setGroupText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGroup(); } }} placeholder="Nova categoria..." />
        <button className="btn btn-sm" onClick={addGroup} disabled={!groupText.trim()}>+</button>
      </div>
    </div>
  );
}

// Aprovações do chamado: solicitar + decidir pendentes.
function ApprovalsPanel({
  approvals,
  agents,
  onRequest,
  onDecide,
}: {
  approvals: Approval[];
  agents: User[];
  onRequest: (subject: string, approverId?: string) => void;
  onDecide: (approval: Approval, approved: boolean) => void;
}) {
  const [subject, setSubject] = useState('');
  const [approverId, setApproverId] = useState('');

  return (
    <div>
      {approvals.length > 0 && (
        <div className="flex flex-col gap-2" style={{ marginBottom: 'var(--s3)' }}>
          {approvals.map((a) => (
            <div key={a.id} className="text-sm" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
              <div className="flex items-center justify-between">
                <strong>{a.subject}</strong>
                <span className={`badge ${a.status === 'approved' ? 'badge-resolved' : a.status === 'rejected' ? 'badge-urgent' : 'badge-waiting_client'}`}>
                  {ApprovalStatusLabels[a.status]}
                </span>
              </div>
              <div className="text-dim text-xs">
                {a.requestedByName ? `por ${a.requestedByName}` : ''}
                {a.approverName ? ` · aprovador ${a.approverName}` : ''}
                {a.decidedByName ? ` · decidido por ${a.decidedByName}` : ''}
              </div>
              {a.status === 'pending' && (
                <div className="flex gap-2 mt-1">
                  <button className="btn btn-sm btn-primary" onClick={() => onDecide(a, true)}>Aprovar</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => onDecide(a, false)}>Reprovar</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="field mb-0">
        <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="O que precisa de aprovação?" />
      </div>
      <select className="select mt-2" value={approverId} onChange={(e) => setApproverId(e.target.value)}>
        <option value="">Aprovador (opcional)</option>
        {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <button className="btn btn-sm btn-primary mt-2" disabled={!subject.trim()} onClick={() => { onRequest(subject.trim(), approverId || undefined); setSubject(''); setApproverId(''); }}>
        Solicitar aprovação
      </button>
    </div>
  );
}

// Visitas técnicas: agendar, listar e mudar status.
function VisitsPanel({
  visits,
  agents,
  onSchedule,
  onUpdate,
}: {
  visits: Visit[];
  agents: User[];
  onSchedule: (payload: { scheduledAt: string; technicianId?: string; address?: string; notes?: string }) => void;
  onUpdate: (visit: Visit, patch: { status?: string }) => void;
}) {
  const [when, setWhen] = useState('');
  const [technicianId, setTechnicianId] = useState('');
  const [address, setAddress] = useState('');

  const submit = () => {
    if (!when) return;
    onSchedule({ scheduledAt: new Date(when).toISOString(), technicianId: technicianId || undefined, address: address || undefined });
    setWhen(''); setTechnicianId(''); setAddress('');
  };

  return (
    <div>
      {visits.length > 0 && (
        <div className="flex flex-col gap-2" style={{ marginBottom: 'var(--s3)' }}>
          {visits.map((v) => (
            <div key={v.id} className="text-sm" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
              <div className="flex items-center justify-between">
                <strong>{new Date(v.scheduledAt).toLocaleString('pt-BR')}</strong>
                <span className={`badge ${v.status === 'done' ? 'badge-resolved' : v.status === 'canceled' ? 'badge-closed' : 'badge-scheduled'}`}>{VisitStatusLabels[v.status]}</span>
              </div>
              <div className="text-dim text-xs">
                {v.technicianName ? `👷 ${v.technicianName}` : 'sem técnico'}{v.address ? ` · 📍 ${v.address}` : ''}
              </div>
              {v.status === 'scheduled' && (
                <div className="flex gap-2 mt-1">
                  <button className="btn btn-sm btn-primary" onClick={() => onUpdate(v, { status: VisitStatus.DONE })}>Concluir</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => onUpdate(v, { status: VisitStatus.CANCELED })}>Cancelar</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="field mb-0"><label className="label">Data e hora</label><input className="input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></div>
      <div className="field mt-2 mb-0"><label className="label">Técnico</label>
        <select className="select" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)}>
          <option value="">—</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className="field mt-2 mb-0"><input className="input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Endereço (opcional)" /></div>
      <button className="btn btn-sm btn-primary mt-2" onClick={submit} disabled={!when}>Agendar visita</button>
    </div>
  );
}

// Editor de causa, solução e causa-raiz do chamado.
function DiagnosisEditor({
  cause,
  solution,
  rootCause,
  onSave,
}: {
  cause?: string;
  solution?: string;
  rootCause?: string;
  onSave: (data: { cause: string; solution: string; rootCause: string }) => void;
}) {
  const [c, setC] = useState(cause ?? '');
  const [s, setS] = useState(solution ?? '');
  const [r, setR] = useState(rootCause ?? '');

  // Sincroniza quando o chamado é recarregado.
  useEffect(() => { setC(cause ?? ''); setS(solution ?? ''); setR(rootCause ?? ''); }, [cause, solution, rootCause]);

  const dirty = c !== (cause ?? '') || s !== (solution ?? '') || r !== (rootCause ?? '');

  return (
    <div className="flex flex-col gap-3">
      <div className="field mb-0">
        <label className="label">Causa</label>
        <textarea className="textarea" rows={2} value={c} onChange={(e) => setC(e.target.value)} placeholder="O que originou o problema" />
      </div>
      <div className="field mb-0">
        <label className="label">Solução</label>
        <textarea className="textarea" rows={2} value={s} onChange={(e) => setS(e.target.value)} placeholder="O que foi feito para resolver" />
      </div>
      <div className="field mb-0">
        <label className="label">Causa raiz</label>
        <textarea className="textarea" rows={2} value={r} onChange={(e) => setR(e.target.value)} placeholder="Causa raiz (análise definitiva)" />
      </div>
      <button className="btn btn-sm btn-primary" disabled={!dirty} onClick={() => onSave({ cause: c, solution: s, rootCause: r })}>
        Salvar diagnóstico
      </button>
    </div>
  );
}

// Painel de mesclagem: seletor com busca de chamados abertos (multi-seleção).
// Carrega os candidatos ativos uma vez e filtra no cliente, mesclando por ids reais.
function MergePanel({
  currentTicketId,
  currentNumber,
  onMerge,
}: {
  currentTicketId: string;
  currentNumber: number;
  onMerge: (ids: string[]) => void;
}) {
  const [all, setAll] = useState<Ticket[]>([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Carga única dos chamados com status ativos (exclui RESOLVED/CLOSED e o atual).
  useEffect(() => {
    const statuses = [
      TicketStatus.OPEN,
      TicketStatus.TRIAGE,
      TicketStatus.IN_PROGRESS,
      TicketStatus.WAITING_CLIENT,
      TicketStatus.WAITING_THIRD_PARTY,
      TicketStatus.SCHEDULED,
    ];
    setLoading(true);
    Promise.all(
      statuses.map((s) =>
        api.get<Paginated<Ticket>>(`/tickets?status=${s}&limit=200`).catch(() => ({ items: [] as Ticket[] })),
      ),
    )
      .then((lists) => {
        const items = lists.flatMap((l) => l.items);
        // Deduplica por id e remove o chamado atual.
        const unique = Array.from(new Map(items.map((t) => [t.id, t])).values()).filter(
          (t) => t.id !== currentTicketId,
        );
        setAll(unique);
      })
      .finally(() => setLoading(false));
  }, [currentTicketId]);

  // Filtra no cliente por número (com/sem #), assunto e solicitante (substring, case-insensitive).
  const term = query.trim().toLowerCase().replace(/^#/, '');
  const filtered = term
    ? all.filter((t) => `#${t.number} ${t.number} ${t.subject} ${t.requesterName ?? ''}`.toLowerCase().includes(term))
    : all;

  const toggle = (ticketId: string) => {
    setSelected((prev) => (prev.includes(ticketId) ? prev.filter((x) => x !== ticketId) : [...prev, ticketId]));
  };

  const submit = () => {
    if (selected.length === 0) return;
    onMerge(selected);
    setSelected([]);
  };

  // Mapa auxiliar para montar os chips dos selecionados.
  const byId = new Map(all.map((t) => [t.id, t]));

  if (loading) return <p className="text-dim text-xs">Carregando chamados...</p>;
  if (all.length === 0) return <p className="text-dim text-xs">Nenhum outro chamado aberto para mesclar.</p>;

  return (
    <div>
      <p className="text-dim text-xs mb-2">Os chamados selecionados serão fechados e relacionados a este (#{currentNumber}).</p>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selected.map((sid) => {
            const t = byId.get(sid);
            return (
              <span
                key={sid}
                className="flex items-center gap-1 text-xs"
                style={{ padding: '2px 8px', borderRadius: 999, background: 'var(--bg-soft, var(--border))' }}
              >
                #{t?.number ?? ''}
                <button
                  type="button"
                  onClick={() => toggle(sid)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text)', lineHeight: 1 }}
                  aria-label={`Remover #${t?.number ?? ''}`}
                >
                  ✕
                </button>
              </span>
            );
          })}
        </div>
      )}

      <input
        className="input"
        placeholder="Busque por número, assunto ou solicitante"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="card card-flush mt-2" style={{ maxHeight: 240, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <p className="text-dim text-xs" style={{ padding: '8px 12px' }}>Nenhum chamado encontrado.</p>
        ) : (
          filtered.map((t) => {
            const isSelected = selected.includes(t.id);
            return (
              <button
                key={t.id}
                type="button"
                className="flex items-center gap-2"
                onClick={() => toggle(t.id)}
                style={{
                  padding: '8px 12px',
                  borderBottom: '1px solid var(--border)',
                  textAlign: 'left',
                  background: isSelected ? 'var(--bg-soft, var(--border))' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                <span style={{ width: 16 }}>{isSelected ? '✓' : ''}</span>
                <span className="flex flex-col">
                  <span className="font-semibold text-sm">#{t.number} — {t.subject}</span>
                  {t.requesterName && <span className="text-dim text-xs">{t.requesterName}</span>}
                </span>
              </button>
            );
          })
        )}
      </div>

      <div className="flex items-center justify-between mt-2">
        <span className="text-dim text-xs">{selected.length} selecionado(s)</span>
        <button className="btn btn-sm" onClick={submit} disabled={selected.length === 0}>Mesclar</button>
      </div>
    </div>
  );
}
